import React from 'react';

const NvidiaBlackwell = () => (
  <article className="news-article-body">
    <p className="news-lead">
      NVIDIA's Blackwell architecture, launched in March 2024, is the hardware foundation
      for the current wave of frontier AI. The B200 and GB200 NVL72 configurations are
      what the biggest AI training runs and inference deployments are running on right now.
      Understanding Blackwell means understanding why AI got dramatically faster and
      cheaper to deploy in 2025.
    </p>

    <h2>What Blackwell actually is</h2>
    <p>
      Blackwell is named after mathematician David Harold Blackwell - continuing NVIDIA's
      tradition of naming GPU architectures after scientists. The B200 GPU is built on
      TSMC's 4NP process node (a customised 4nm variant), and packs 208 billion transistors -
      making it the largest chip ever manufactured at the time of launch.
    </p>
    <p>
      Key specs that matter for AI:
    </p>
    <ul>
      <li><strong>FP8 training performance:</strong> 20 petaFLOPS per GPU - 2.5x improvement over H100</li>
      <li><strong>HBM3e memory:</strong> 192GB per GPU (same as MI300X), with 8 TB/s bandwidth</li>
      <li><strong>Second-generation transformer engine:</strong> Hardware acceleration for the attention calculations that dominate transformer model compute</li>
      <li><strong>NVLink 5:</strong> 1.8 TB/s GPU-to-GPU bandwidth within a rack</li>
    </ul>

    <h2>GB200 NVL72 - the rack-scale system</h2>
    <p>
      The GB200 NVL72 is the most important Blackwell configuration for large-scale AI.
      It combines 36 Grace CPUs and 72 B200 GPUs in a single liquid-cooled rack - connected
      by NVLink so all 72 GPUs share a unified memory pool of 13.8TB.
    </p>
    <p>
      Why does that matter? Large model training requires frequent communication between
      GPUs. With NVL72, that communication happens at NVLink speeds (1.8 TB/s) rather than
      over the network. Training throughput for a frontier model on one NVL72 rack can
      match what previously required multiple separate server cabinets.
    </p>

    <h2>Supply chain and the H100/B200 transition</h2>
    <p>
      The Blackwell launch was notable not just for specs but for supply chain complexity.
      NVIDIA, TSMC, SK Hynix (HBM3e supplier), and system integrators had to coordinate
      a product launch involving some of the most advanced semiconductor manufacturing
      processes in the world. Initial delays in late 2024 created a period where H100
      supply was winding down but B200 availability was limited.
    </p>
    <p>
      By Q1 2025, Blackwell supply had ramped significantly. NVIDIA reported its first
      quarter of over $20 billion in data centre revenue - driven almost entirely by
      Blackwell sales.
    </p>

    <h2>Inference efficiency - the often-overlooked story</h2>
    <p>
      Much coverage focuses on training. But Blackwell's biggest near-term impact may be
      inference. The B200's FP4 precision mode enables dramatically faster inference with
      minimal quality loss - important because inference (running models to serve users)
      is now the majority of AI compute spending for many companies.
    </p>
    <p>
      NVIDIA's own benchmarks show B200 handling 30x more inference tokens per second
      than H100 for certain workloads. Even accounting for benchmark optimism, the
      inference efficiency gains are substantial.
    </p>

    <div className="news-insight-box">
      <h3>The big lesson</h3>
      <p>
        Blackwell represents NVIDIA's ability to continue delivering large generational leaps
        in AI compute performance. The 2.5x training improvement and 30x inference gain figures
        are why hyperscalers committed to multi-billion dollar Blackwell orders before most
        customers had seen the hardware. That trust - built over years of delivered roadmap
        promises - is as important to NVIDIA's position as the silicon itself.
      </p>
    </div>
  </article>
);

export default NvidiaBlackwell;
